import { type AuthorizationParams, createAuth0 } from "@auth0/auth0-vue";
import { jwtDecode } from "jwt-decode";
import { defineStore } from "pinia";
import { computed } from "vue";

const authorizationParams: AuthorizationParams = {
  audience: import.meta.env.VITE_AUTH0_AUDIENCE,
  redirect_uri: window.location.origin,
};

// Read the “ui_locales” query parameter from the URL and use it for the auth0 authorization params' “ui_locales” property. This allows the app routes to serve as auth0 “language_switcher_endpoint”s which are able to generate an “/authorize” URL with proper state which is **not** possible from within the universal login template.
const url = new URL(window.location.href);
const uiLocales = url.searchParams.get("ui_locales");
if (uiLocales) {
  authorizationParams.ui_locales = uiLocales;
}

export const auth0Plugin = createAuth0({
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
  domain: import.meta.env.VITE_AUTH0_DOMAIN,
  useRefreshTokens: true,
  cacheLocation: "localstorage",
  authorizationParams,
});

export const useAuth = defineStore("auth", () => {
  const isAuthenticated = computed(() => {
    return !auth0Plugin.isLoading ? false : auth0Plugin.isAuthenticated.value;
  });

  const userId = computed<string | undefined>(() => auth0Plugin.user.value?.account_id);

  async function getAccessToken(): Promise<string> {
    try {
      const accessToken = await auth0Plugin.getAccessTokenSilently();
      if (!accessToken) {
        throw new Error("Auth0's `getAccessTokenSilently` did not return an access token!");
      }

      return accessToken;
    } catch {
      await logout();
      return "";
    }
  }

  function isRegistrationLinkTokenExpired(token: string): boolean {
    try {
      const sessionToken = jwtDecode(token);
      if (sessionToken.exp === undefined) {
        return true;
      }

      const currentDate = new Date();
      const expireDate = new Date(sessionToken.exp * 1000);
      return currentDate > expireDate;
    } catch {
      return false;
    }
  }

  const logout = async (redirectUrl?: string) => {
    await auth0Plugin.logout({
      logoutParams: {
        returnTo: redirectUrl ?? window.location.origin,
        client_id: import.meta.env.VITE_AUTH0_CLIENT_ID,
      },
    });
  };

  /**
   * Enrolls the current user with a specific factor using the “login with redirect” utility.
   */
  async function enrollWithFactor(locale: string) {
    const url = new URL(window.location.href);
    // Used to automatically open the MFA section on return.
    url.hash = "mfa";
    await auth0Plugin.loginWithRedirect({
      authorizationParams: {
        redirect_uri: url.toString(),
        enroll: "1",
        ui_locales: locale,
      },
    });
  }

  return {
    isAuthenticated,
    userId,
    logout,
    isRegistrationLinkTokenExpired,
    enrollWithFactor,
    getAccessToken,
  };
});
